import {DataGrid, GridActionsCellItem, GridRowParams} from "@mui/x-data-grid";
import React, {useCallback, useEffect, useMemo, useState} from "react";

import {
    Box,
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    FormControl,
    Input,
    InputLabel,
    List,
    ListItem,
    MenuItem,
    Modal,
    Paper,
    Select,
    SelectChangeEvent,
    Stack,
    styled,
    Typography
} from "@mui/material";
import {Edit, OpenInFull, Workspaces} from "@mui/icons-material";
import {VersionItem} from "../../Components/VersionItem";
import {useDocumentTitle} from "../../Hooks/useDocumentTitle";
import {useApplicationContext} from "../../ApplicationContext";
import {GetVersionData, GetVersionDataCustomer} from "../../apiClient/Api";
import {MarkdownRenderer} from "../../Components/MarkdownRenderer";
import {useParams} from "react-router";
import {useTranslation} from "react-i18next";


const modalStyle = {
    position: 'absolute' as 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: 400,
    bgcolor: 'background.paper',
    border: '2px solid #000',
    boxShadow: 24,
    p: 4,
};

type RouteParams = {
    link: string
}

type HostSystemOption = {
    id: string
    name: string
}
export const Versions = () => {
    useDocumentTitle("base.pages.versions")
    const { t } = useTranslation();
    const [data, setData] = useState<(GetVersionData | GetVersionDataCustomer)[]>([])
    const [currentVersion, setCurrentVersionData] = useState<GetVersionData | GetVersionDataCustomer | null>(null);
    const [isFetching, setIsFetching] = useState(false);
    const [isData, setIsData] = useState(false);
    const [open, setOpen] = useState(false);
    const [counter, setCounter] = useState(0)
    const [selectedHostSystems, setSelectedHostSystems] = useState<string[]>([]);
    const [availableHostSystems, setAvailableHostSystems] = useState<HostSystemOption[]>([]);
    const context = useApplicationContext();
    const params = useParams<RouteParams>()
    var controller = context.api.version;
    const handleOpen = () => setOpen(true);
    const handleClose = () => setOpen(false);
    const handleSave = async () => {
        setOpen(false)
        if (currentVersion != null) {
            var value = await controller.versionUpdateVersion(currentVersion)
        }
    };

    useEffect(() => {
        setSelectedHostSystems([]);
        setAvailableHostSystems([]);
    }, [params.link])

    const hostSystemLabels = useMemo(() => {
        const labels: Record<string, string> = {};
        availableHostSystems.forEach((system) => {
            labels[system.id] = system.name || system.id;
        });
        return labels;
    }, [availableHostSystems]);

    const mergeHostSystems = useCallback((versions: (GetVersionData | GetVersionDataCustomer)[], shouldReset: boolean) => {
        const collected = new Map<string, string>();
        const collect = (version: GetVersionData | GetVersionDataCustomer) => {
            version.files?.forEach((file) => {
                if (file.hostSystemId) {
                    const existing = collected.get(file.hostSystemId);
                    const nextName = file.hostSystemName ?? existing ?? '';
                    collected.set(file.hostSystemId, nextName);
                }
            });
            version.childVersions?.forEach(collect);
        };
        versions.forEach(collect);

        setAvailableHostSystems((prev) => {
            const combined = new Map<string, string>();
            if (!shouldReset) {
                prev.forEach((option) => combined.set(option.id, option.name));
            }
            collected.forEach((name, id) => {
                const existing = combined.get(id);
                if (!existing || name) {
                    combined.set(id, name || existing || '');
                }
            });
            return Array.from(combined.entries())
                .map(([id, name]) => ({ id, name }))
                .sort((a, b) => a.name.localeCompare(b.name));
        });
    }, []);

    /**
     * Checks if the provided string is a versionId in format "V{major}.{minor}.{patch}_{productId}"
     * or tries to parse version information from URL hash
     */
    const parseVersionFromLink = (link: string): { productId: string; versionKey?: { major: number; minor: number; patch: number; productId: string } } | null => {
        // Check if format is like "V6.5.21_60f09d44-b6ca-471a-928c-a21b14f1559e"
        const versionLinkMatch = link.match(/^V(\d+)\.(\d+)\.(\d+)_([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})$/i);
        if (versionLinkMatch) {
            return {
                productId: versionLinkMatch[4],
                versionKey: {
                    major: parseInt(versionLinkMatch[1]),
                    minor: parseInt(versionLinkMatch[2]),
                    patch: parseInt(versionLinkMatch[3]),
                    productId: versionLinkMatch[4]
                }
            };
        }
        
        // Otherwise assume it's a productId
        const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
        if (uuidRegex.test(link)) {
            return { productId: link };
        }
        
        return { productId: link };
    };

    const fetchData = useCallback(async () => {
        if (!params.link)
            return;

        setIsFetching(true)
        try {
            const parsed = parseVersionFromLink(params.link);
            if (!parsed) return;

            // If we have version key, load single version
            if (parsed.versionKey) {
                const value = context.isInternal
                    ? await controller.versionGetVersionByKey(parsed.versionKey)
                    : await controller.versionGetVersionByKeyForCustomer(parsed.versionKey);
                if (value && value.data) {
                    setData([value.data])
                    mergeHostSystems([value.data], true)
                }
            } else {
                // Load all versions for a product
                const queryPayload = selectedHostSystems.length > 0 ? { hostSystemIds: selectedHostSystems } : undefined;
                const linkWithQuery = (() => {
                    if (!queryPayload || selectedHostSystems.length === 0) {
                        return parsed.productId;
                    }
                    const searchParams = new URLSearchParams();
                    selectedHostSystems.forEach((id) => searchParams.append('hostSystemIds', id));
                    return `${parsed.productId}?${searchParams.toString()}`;
                })();
                const value = context.isInternal
                    ? await controller.versionGetVersions(linkWithQuery)
                    : await controller.versionGetCustomerVersions(parsed.productId, queryPayload);
                if (value && value.data) {
                    setData(value.data)
                    mergeHostSystems(value.data, selectedHostSystems.length === 0)
                }
            }
        } catch (error) {
            context.apiError?.(error)
        } finally {
            setIsFetching(false)
        }
    }, [params.link, selectedHostSystems, context.isInternal, controller, mergeHostSystems])

    useEffect(() => {
        fetchData();
    }, [fetchData])

    useEffect(() => {
        // Scroll to version if hash is present in URL
        if (window.location.hash && data.length > 0) {
            const versionId = window.location.hash.substring(1); // Remove # from hash
            const element = document.getElementById(versionId);
            if (element) {
                setTimeout(() => {
                    element.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }, 300); // Small delay to ensure DOM is ready
            }
        }
    }, [data]);

    const reloadCallback = useCallback(async () => {
        await fetchData()
    }, [fetchData])

    const handleHostSystemChange = (event: SelectChangeEvent<string[]>) => {
        setSelectedHostSystems(event.target.value as string[]);
    };

    const handleRemoveHostSystem = (systemToRemove: string) => {
        setSelectedHostSystems((prev) => prev.filter(s => s !== systemToRemove));
    };

    return (
        <>
            <Box sx={{width: '100%', padding: 1}}>
                <Box sx={{ mb: 2 }}>
                    <FormControl fullWidth>
                        <InputLabel id="host-system-filter-label">{t('versions.filterByHostSystem')}</InputLabel>
                        <Select
                            labelId="host-system-filter-label"
                            multiple
                            value={selectedHostSystems}
                            onChange={handleHostSystemChange}
                            label={t('versions.filterByHostSystem')}
                            disabled={isFetching}
                            renderValue={(selected) => (
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                    {selected.map((value) => (
                                        <Chip 
                                            key={value} 
                                            label={hostSystemLabels[value] ?? value}
                                            onDelete={() => handleRemoveHostSystem(value)}
                                            onMouseDown={(event) => {
                                                event.stopPropagation();
                                            }}
                                        />
                                    ))}
                                </Box>
                            )}
                        >
                            {availableHostSystems.map((system) => (
                                <MenuItem key={system.id} value={system.id}>
                                    {system.name || system.id}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Box>
                
                {isFetching ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '50vh', gap: 2 }}>
                        <CircularProgress size={60} />
                        <Typography variant="h6" color="text.secondary">
                            {t('versions.loading')}
                        </Typography>
                    </Box>
                ) : (
                    <Stack spacing={2}>
                    {data.map(major => {
                        return (
                            <React.Fragment key={'versionElement_'+major.versionId}>

                                <Paper sx={{paddingLeft: 5, paddingTop: 2, paddingBottom: 2, paddingRight: 5}} key={"header_"+major.versionId}>
                                    <Typography variant={"h2"} color={"secondary"} sx={{ textAlign: 'center' }}>{t('version.item.versionNumber', { version: major.major })}</Typography>
                                    {(major?.header?.length ?? 0) > 0 && <>
                                        <Typography sx={{fontSize: 22}} variant={"h3"} color={"secondary"}>{t('version.item.notes')}</Typography>
                                        <MarkdownRenderer>{major.header}</MarkdownRenderer>
                                    </>}

                                    <Button sx={{marginTop: 3, marginBottom: 3}} fullWidth size={"large"} variant={"outlined"}
                                            onClick={() => {
                                                const element = document.getElementById(`V${major.major}.${major.minor}.${major.patch}`);
                                                if (element) {
                                                    // Will scroll smoothly to the top of the next section
                                                    element.scrollIntoView({behavior: 'smooth'});
                                                }
                                            }}>
                                        {(major.files?.length ?? 0) > 0 ? t('version.item.crossVersionInformationAndFiles') : t('version.item.crossVersionInformation')}
                                    </Button>
                                </Paper>

                                {major?.childVersions?.map(minor => <>
                                    <Paper sx={{paddingLeft: 5, paddingTop: 2, paddingBottom: 2, paddingRight: 5}} key={"header_"+minor.versionId}>
                                        <Typography variant={"h3"} color={"primary"} sx={{ textAlign: 'center' }}>{t('version.item.versionNumber', { version: `${major.major}.${minor.minor}` })}</Typography>
                                        {(minor?.header?.length ?? 0) > 0 && <>
                                            <Typography sx={{fontSize: 22}} variant={"h4"} color={"secondary"}>{t('version.item.notes')}</Typography>
                                            <MarkdownRenderer>{minor.header}</MarkdownRenderer>
                                        </>}

                                        <Button sx={{marginTop: 3, marginBottom: 3}} fullWidth size={"large"} variant={"outlined"}
                                                onClick={() => {
                                                    const element = document.getElementById(`V${minor.major}.${minor.minor}.${minor.patch}`);
                                                    if (element) {
                                                        // Will scroll smoothly to the top of the next section
                                                        element.scrollIntoView({behavior: 'smooth'});
                                                    }
                                                }}>
                                            {(minor.files?.length ?? 0) > 0 ? t('version.item.crossVersionInformationAndFiles') : t('version.item.crossVersionInformation')}
                                        </Button>
                                    </Paper>
                                    {minor?.childVersions?.map(patch => <>
                                        <VersionItem data={patch} reloadCallback={reloadCallback} key={patch.versionId}/>
                                    </>)}
                                    <VersionItem data={minor} reloadCallback={reloadCallback} key={minor.versionId}/>
                                </>)}

                                <VersionItem data={major} reloadCallback={reloadCallback} key={major.versionId}/>
                            </React.Fragment>
                        );
                    })}
                </Stack>
                )}
            </Box>
        </>
    );
}


